import { mediaURL } from "./media";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiFetch,
  postJSON,
  type CheckoutInput,
  type CheckoutResult,
  type Order,
  type Product,
  type ProductInput,
  type PublicOrder,
  type PublicStore,
  type Store,
  type StoreInput,
} from "./api";

// ── Owner (dashboard) ───────────────────────────────────────────────────

export function useStore(siteId: string | undefined) {
  return useQuery({
    queryKey: ["store", siteId],
    queryFn: () => apiFetch<Store>(`/sites/${siteId}/store`),
    enabled: Boolean(siteId),
    retry: false,
  });
}

export function useUpdateStore(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StoreInput) =>
      apiFetch<Store>(`/sites/${siteId}/store`, { method: "PUT", body: JSON.stringify(input) }),
    onSuccess: (store) => queryClient.setQueryData(["store", siteId], store),
  });
}

export function useProducts(siteId: string | undefined) {
  return useQuery({
    queryKey: ["products", siteId],
    queryFn: () => apiFetch<{ products: Product[] }>(`/sites/${siteId}/store/products`),
    select: (data) => data.products,
    enabled: Boolean(siteId),
  });
}

export function useSaveProduct(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: ProductInput }) =>
      id
        ? apiFetch<Product>(`/sites/${siteId}/store/products/${id}`, {
            method: "PUT",
            body: JSON.stringify(input),
          })
        : postJSON<Product>(`/sites/${siteId}/store/products`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["products", siteId] }),
  });
}

export function useDeleteProduct(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiFetch<void>(`/sites/${siteId}/store/products/${productId}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["products", siteId] }),
  });
}

/** Uploads a product photo and returns the URL to store on the product. */
export async function uploadProductImage(siteId: string, file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const result = await apiFetch<{ url: string }>(`/sites/${siteId}/store/images`, {
    method: "POST",
    body: form,
  });
  return mediaURL(result.url);
}

export function useOrders(siteId: string | undefined) {
  return useQuery({
    queryKey: ["orders", siteId],
    queryFn: () => apiFetch<{ orders: Order[] }>(`/sites/${siteId}/store/orders`),
    select: (data) => data.orders,
    enabled: Boolean(siteId),
    refetchInterval: 30_000,
  });
}

export function useUpdateOrderStatus(siteId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, status }: { orderId: string; status: "paid" | "fulfilled" | "cancelled" }) =>
      apiFetch<Order>(`/sites/${siteId}/store/orders/${orderId}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["orders", siteId] });
      void queryClient.invalidateQueries({ queryKey: ["products", siteId] });
    },
  });
}

// ── Storefront (public, no session) ─────────────────────────────────────

export function useStorefront(slug: string | undefined) {
  return useQuery({
    queryKey: ["storefront", slug],
    queryFn: () => apiFetch<PublicStore>(`/public/stores/${slug}`),
    enabled: Boolean(slug),
    retry: false,
    staleTime: 15_000,
  });
}

export function placeOrder(slug: string, input: CheckoutInput) {
  return postJSON<CheckoutResult>(`/public/stores/${slug}/orders`, input);
}

export function usePublicOrder(slug: string | undefined, orderId: string | undefined, token: string | null) {
  return useQuery({
    queryKey: ["public-order", slug, orderId, token],
    queryFn: () =>
      apiFetch<PublicOrder>(
        `/public/stores/${slug}/orders/${orderId}?token=${encodeURIComponent(token ?? "")}`,
      ),
    enabled: Boolean(slug && orderId && token),
    retry: false,
  });
}

export function startOrderPayment(slug: string, orderId: string, token: string) {
  return postJSON<{ paymentUrl: string }>(
    `/public/stores/${slug}/orders/${orderId}/payment?token=${encodeURIComponent(token)}`,
    {},
  );
}

export function approveTestPayment(slug: string, orderId: string, token: string) {
  return postJSON<PublicOrder>(
    `/public/stores/${slug}/orders/${orderId}/test-payment?token=${encodeURIComponent(token)}`,
    {},
  );
}
